import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ROL_ETIKET, type Rol } from '../lib/api';
import { useOturum } from '../lib/oturum';
import { KullaniciYonetimi } from './AdminPanel';

/**
 * SA-01 — TANIMLAR (devir dokumani Bolum 6.10, prototip `vTanim`).
 *
 * K9: sabit listeleri YALNIZ SEF (admin) yonetir. Formen/ekip basi yeni
 * unite/kisi ekleyemez — gerekce DB kirlenmesini onleme.
 *
 * ⚠️ Bu kural soyut degil: yillik Excel'de zaten olmus. Gercek veride
 *    `AQC3/AQc3`, `Diğer/Dİğer`, `WHR/Whr`, `Silo/SİLO/Slio 3` varyantlari var.
 *
 * Bu ekran musteri-bagimsiz admin panelinin v1 cekirdegi — SOCAR/Tupras
 * fazinda musteri-tipi sablonlari da buraya gelecek.
 */

interface Tanimlar {
  uniteler: {
    id: string;
    ad: string;
    aktif: boolean;
    /** Ünite grubu (SOCAR: STAR / Petkim / iç / dış proje). */
    grup?: string | null;
    /** Bu bölgede K5 izin kâğıdı istenmez (dış proje). */
    izinGerekmez?: boolean;
  }[];
  ilgililer: {
    id: string;
    ad: string;
    email: string;
    birim: string | null;
    aktif: boolean;
    /** Sabit CC — işe seçilmese bile her mailde bulunur. */
    herMailde: boolean;
  }[];
  calisanlar: { id: string; ad: string; rol: Rol; sicilNo: number }[];
}

export function TanimlarEkrani() {
  const { ben } = useOturum();
  const [t, setT] = useState<Tanimlar | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /**
   * KAPALI KAYITLAR VARSAYILAN OLARAK GİZLİ.
   *
   * "kaldır" silmez, pasife alır (geçmiş işlerin referansı korunmalı) — ama
   * kaldırılan kayıt listede durmaya devam edince şef onu SİLMEK ister.
   * İstenen şey kaydın yok olması değil, GÖZDEN kalkması; ikisi ayrıştırıldı.
   */
  const [kapaliGoster, setKapaliGoster] = useState(false);

  const yukle = useCallback(async () => {
    try {
      setT(await api.get<Tanimlar>('/tanimlar?hepsi=1'));
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Yüklenemedi');
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  if (!t) return <p className="m-6 text-sm text-wait">Yükleniyor…</p>;

  const uniteler = kapaliGoster ? t.uniteler : t.uniteler.filter((u) => u.aktif);
  const ilgililer = kapaliGoster ? t.ilgililer : t.ilgililer.filter((c) => c.aktif);
  const gizliUnite = t.uniteler.length - t.uniteler.filter((u) => u.aktif).length;
  const gizliIlgili = t.ilgililer.length - t.ilgililer.filter((c) => c.aktif).length;
  /*
   * ÜNİTE GRUPLARI yalnız kullanılan şantiyede görünür (SOCAR, 09.10.2026).
   * Nuh ve Akçansa'da hiçbir ünite gruplanmadığı için ekranları değişmez.
   */
  const grupKullaniliyor = t.uniteler.some((u) => u.grup || u.izinGerekmez);
  const mevcutGruplar = [
    ...new Set(t.uniteler.map((u) => u.grup).filter((g): g is string => !!g)),
  ].sort((a, b) => a.localeCompare(b, 'tr'));

  return (
    <div className="mx-auto max-w-6xl p-4 lg:p-6">
      <h1 className="text-xl font-semibold text-navy">
        Tanımlar — {ben?.santiye.musteriAd ?? ''}
      </h1>
      <p className="mb-3 text-sm text-wait">
        Sabit listeler yalnız <strong>şef (admin)</strong> tarafından yönetilir. Formen/ekip başı
        yeni kayıt ekleyemez — veritabanı kirlenmesini önler (K9).
      </p>

      {/*
        Kaydın kendisi SİLİNMEZ — geçmiş işler ona bağlı, iz kaydındaki adı bu
        satırdan gelir. Kaldırılan yalnız GÖRÜNÜRLÜKTÜR ve bu anahtarla geri gelir.
      */}
      <label className="mb-5 flex w-fit items-center gap-2 text-sm text-ink-2">
        <input
          type="checkbox"
          checked={kapaliGoster}
          onChange={(e) => setKapaliGoster(e.target.checked)}
        />
        Kaldırılmış kayıtları da göster
      </label>

      {hata && (
        <p className="mb-4 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
      )}

      <div className="mb-8 grid gap-4 lg:grid-cols-2">
        {/* ÜNİTELER */}
        <Kolon baslik={`Üniteler (${t.uniteler.filter((u) => u.aktif).length})`}>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {uniteler.map((u) => (
              <div key={u.id}>
                <Satir
                  ad={u.ad}
                  aktif={u.aktif}
                  onDurum={async () => {
                    await api
                      .patch(`/uniteler/${u.id}/durum`, { aktif: !u.aktif })
                      .catch((e: Error) => setHata(e.message));
                    await yukle();
                  }}
                />
                {grupKullaniliyor && u.aktif && (
                  <UniteGrup u={u} onDegisti={yukle} onHata={setHata} />
                )}
              </div>
            ))}
          </div>
          {grupKullaniliyor && (
            <>
              <datalist id="unite-gruplari">
                {mevcutGruplar.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
              <p className="mt-2 text-xs leading-relaxed text-wait">
                Grup rapor ve süzgeçte ayrım içindir. <strong>İzin gerekmez</strong> işaretli
                bölgede iş izni kâğıdı istenmez ve teslim onu beklemez.
              </p>
            </>
          )}
          <Gizli sayi={gizliUnite} goster={kapaliGoster} />
          <Ekle
            yer="Ünite adı — ör. Fırın 3"
            onEkle={async (ad) => {
              await api.post('/uniteler', { ad });
              await yukle();
            }}
            onHata={setHata}
          />
          <p className="mt-2 text-xs leading-relaxed text-wait">
            Aynı ad farklı yazımla ikinci kez girilemez — kirlilik orada başlıyor.
          </p>
        </Kolon>

        {/* İLGİLİ KİŞİLER */}
        <Kolon baslik={`İlgili kişiler / mailler (${t.ilgililer.filter((c) => c.aktif).length})`}>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {ilgililer.map((c) => (
              <IlgiliSatir
                key={c.id}
                c={c}
                onDegisti={yukle}
                onHata={setHata}
              />
            ))}
          </div>
          <Gizli sayi={gizliIlgili} goster={kapaliGoster} />
          <IlgiliEkle onEklendi={yukle} onHata={setHata} />
          <p className="mt-2 text-xs leading-relaxed text-wait">
            İş kapanınca ve günlük rapor gönderilince mail bu kişilere gider.
            <strong> Adres yanlışsa mail sessizce boşa gider</strong> — “düzelt” ile
            değiştirilebilir.
          </p>
        </Kolon>
      </div>

      {/* ÇALIŞANLAR — kullanıcı yönetimiyle aynı şey, tek yerde */}
      {ben?.sefYetkisi && <KullaniciYonetimi kapaliGoster={kapaliGoster} />}
    </div>
  );
}

/**
 * "N kayıt gizli" — kaldırılan şeyin YOK OLMADIĞINI söyleyen tek işaret.
 * Bu satır olmasaydı liste sessizce kısalır ve kaydın hâlâ durduğu yalnız
 * anahtarı fark eden kullanıcıya görünürdü.
 */
/**
 * ÜNİTE GRUBU ve İZİN GEREKMEZ — K9: yalnız şef. Grup kutudan çıkınca
 * kaydedilir; değişmediyse istek gitmez (iz kaydına boş satır düşmesin).
 */
function UniteGrup({
  u,
  onDegisti,
  onHata,
}: {
  u: Tanimlar['uniteler'][number];
  onDegisti: () => void | Promise<void>;
  onHata: (h: string) => void;
}) {
  const [grup, setGrup] = useState(u.grup ?? '');
  const kaydet = async (govde: { grup?: string | null; izinGerekmez?: boolean }) => {
    try {
      await api.patch(`/uniteler/${u.id}`, govde);
      await onDegisti();
    } catch (e) {
      onHata(e instanceof Error ? e.message : 'Kaydedilemedi');
    }
  };
  return (
    <div className="mb-1 ml-3 flex flex-wrap items-center gap-2 text-xs text-wait">
      <input
        list="unite-gruplari"
        className="w-32 rounded-(--radius-mod) border border-line-2 px-2 py-0.5 text-xs outline-none focus:border-navy"
        placeholder="grup"
        maxLength={40}
        value={grup}
        onChange={(e) => setGrup(e.target.value)}
        onBlur={() => {
          if (grup.trim() !== (u.grup ?? '')) void kaydet({ grup: grup.trim() || null });
        }}
      />
      <label className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={!!u.izinGerekmez}
          onChange={(e) => void kaydet({ izinGerekmez: e.target.checked })}
        />
        izin gerekmez
      </label>
    </div>
  );
}

function Gizli({ sayi, goster }: { sayi: number; goster: boolean }) {
  if (goster || sayi === 0) return null;
  return (
    <p className="mt-2 text-xs text-faint">
      {sayi} kaldırılmış kayıt gizli — yukarıdaki kutudan görebilirsiniz.
    </p>
  );
}

function Kolon({ baslik, children }: { baslik: string; children: React.ReactNode }) {
  return (
    <section className="rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-4">
      <h2 className="mb-3 text-sm font-semibold text-navy">{baslik}</h2>
      {children}
    </section>
  );
}

/**
 * İLGİLİ KİŞİ SATIRI — düzeltilebilir.
 *
 * ⚠️ Adres düzeltmesi olmadan bir yazım hatası SESSİZ bir arızaydı: mail
 *    kuyruğa girer, sağlayıcı kabul eder, sistemde `gönderildi` görünür ama
 *    müşteriye "iskele kuruldu" haberi hiç ulaşmaz.
 *
 * Kişi SİLİNMEZ — geçmiş işler ona bağlı. Yanlış kişi eklendiyse kaldırılır.
 */
function IlgiliSatir({
  c,
  onDegisti,
  onHata,
}: {
  c: {
    id: string;
    ad: string;
    email: string;
    birim: string | null;
    aktif: boolean;
    herMailde: boolean;
  };
  onDegisti: () => Promise<void> | void;
  onHata: (h: string) => void;
}) {
  const [duzenle, setDuzenle] = useState(false);
  const [ad, setAd] = useState(c.ad);
  const [email, setEmail] = useState(c.email);
  const [birim, setBirim] = useState(c.birim ?? '');
  const [herMailde, setHerMailde] = useState(c.herMailde);
  const [mesgul, setMesgul] = useState(false);

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-2 py-1 text-sm outline-none focus:border-navy';

  async function kaydet(e: FormEvent) {
    e.preventDefault();
    setMesgul(true);
    try {
      await api.patch(`/ilgili-kisiler/${c.id}`, { ad, email, birim, herMailde });
      setDuzenle(false);
      await onDegisti();
    } catch (err) {
      onHata(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setMesgul(false);
    }
  }

  if (duzenle) {
    return (
      <form
        onSubmit={kaydet}
        className="space-y-1.5 rounded-(--radius-mod) border border-navy/30 bg-navy/5 p-2"
      >
        <input className={gir} value={ad} onChange={(e) => setAd(e.target.value)} autoFocus />
        <input
          className={gir}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className={gir}
          placeholder="Birim — ör. Makine Bakım"
          value={birim}
          onChange={(e) => setBirim(e.target.value)}
        />
        {/*
          SABİT CC — müşteri isteği: "her mailde de bulunmalıdır". İşe tek tek
          eklemeye bırakılsaydı er ya da geç unutulur, unutulduğu da fark
          edilmezdi.
        */}
        <label className="flex items-center gap-2 py-0.5 text-xs text-ink-2">
          <input
            type="checkbox"
            checked={herMailde}
            onChange={(e) => setHerMailde(e.target.checked)}
          />
          Her mailde bulunsun <span className="text-wait">(sabit CC)</span>
        </label>
        <div className="flex gap-1.5">
          <button
            disabled={mesgul || ad.trim().length < 2}
            className="rounded-(--radius-mod) bg-navy px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            {mesgul ? '…' : 'Kaydet'}
          </button>
          <button
            type="button"
            onClick={() => setDuzenle(false)}
            className="rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-surface-2 hover:text-ink"
          >
            Vazgeç
          </button>
        </div>
      </form>
    );
  }

  return (
    <Satir
      ad={c.ad}
      yan={c.email}
      rozet={c.herMailde ? 'her mailde' : undefined}
      aktif={c.aktif}
      onDuzelt={() => setDuzenle(true)}
      onDurum={async () => {
        await api
          .patch(`/ilgili-kisiler/${c.id}/durum`, { aktif: !c.aktif })
          .catch((e: Error) => onHata(e.message));
        await onDegisti();
      }}
    />
  );
}

function Satir({
  ad,
  yan,
  rozet,
  aktif,
  onDurum,
  onDuzelt,
}: {
  ad: string;
  yan?: string;
  rozet?: string;
  aktif: boolean;
  onDurum: () => void | Promise<void>;
  onDuzelt?: () => void;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded-(--radius-mod) border border-line/70 px-3 py-2 text-sm ${
        aktif ? '' : 'opacity-45'
      }`}
    >
      <span className="min-w-0 flex-1">
        {ad}
        {yan && <span className="ml-2 text-xs text-wait">{yan}</span>}
        {rozet && (
          <span className="rozet ml-2 border-navy-line bg-navy-bg text-[10px] text-navy">
            {rozet}
          </span>
        )}
      </span>
      {onDuzelt && (
        <button
          onClick={onDuzelt}
          className="rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-navy-bg hover:text-navy"
        >
          düzelt
        </button>
      )}
      {/* Silme DEĞİL, pasife alma — geçmiş işlerin referansı korunur. */}
      <button onClick={() => void onDurum()} className="rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-surface-2 hover:text-ink">
        {aktif ? 'kaldır' : 'geri al'}
      </button>
    </div>
  );
}

function Ekle({
  yer,
  onEkle,
  onHata,
}: {
  yer: string;
  onEkle: (ad: string) => Promise<void>;
  onHata: (h: string) => void;
}) {
  const [ad, setAd] = useState('');
  const [mesgul, setMesgul] = useState(false);

  async function gonder(e: FormEvent) {
    e.preventDefault();
    if (!ad.trim()) return;
    setMesgul(true);
    try {
      await onEkle(ad.trim());
      setAd('');
    } catch (err) {
      onHata(err instanceof Error ? err.message : 'Eklenemedi');
    } finally {
      setMesgul(false);
    }
  }

  return (
    <form onSubmit={gonder} className="mt-3 flex gap-2">
      <input
        className="flex-1 rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm"
        placeholder={yer}
        value={ad}
        onChange={(e) => setAd(e.target.value)}
      />
      <button
        disabled={mesgul || !ad.trim()}
        className="rounded-(--radius-mod) bg-navy px-3 py-2 text-sm text-white disabled:opacity-40"
      >
        + ekle
      </button>
    </form>
  );
}

function IlgiliEkle({
  onEklendi,
  onHata,
}: {
  onEklendi: () => void | Promise<void>;
  onHata: (h: string) => void;
}) {
  const [ad, setAd] = useState('');
  const [email, setEmail] = useState('');
  const [birim, setBirim] = useState('');
  const [mesgul, setMesgul] = useState(false);

  async function gonder(e: FormEvent) {
    e.preventDefault();
    setMesgul(true);
    try {
      await api.post('/ilgili-kisiler', { ad, email, birim: birim || undefined });
      setAd('');
      setEmail('');
      setBirim('');
      await onEklendi();
    } catch (err) {
      onHata(err instanceof Error ? err.message : 'Eklenemedi');
    } finally {
      setMesgul(false);
    }
  }

  const gir = 'rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm';

  return (
    <form onSubmit={gonder} className="mt-3 flex flex-wrap gap-2">
      <input
        className={`${gir} flex-1`}
        placeholder="Ad soyad"
        value={ad}
        onChange={(e) => setAd(e.target.value)}
        required
      />
      <input
        className={`${gir} flex-1`}
        type="email"
        placeholder="e-posta"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <input
        className={gir}
        placeholder="birim (ops.)"
        value={birim}
        onChange={(e) => setBirim(e.target.value)}
      />
      <button
        disabled={mesgul}
        className="rounded-(--radius-mod) bg-navy px-3 py-2 text-sm text-white disabled:opacity-40"
      >
        + ekle
      </button>
    </form>
  );
}
